import {
    addDays,
    addMonths,
    addWeeks,
    differenceInDays,
    differenceInMonths,
    differenceInWeeks,
    format,
    parseISO,
} from 'date-fns';
import { DateFormatEnum } from '../types/date-format.enum';
import { toDate } from 'date-fns-tz';
import { DateTimeFormatOptions } from '../types/date-time-format-options.type';

/**
 * Converts ISO 8601 date string to another date format
 * @param date ISO 8601 string
 * @param dateFormat output date format
 */
export function formatIsoString(
    date: string,
    dateFormat: DateFormatEnum,
): string {
    try {
        return format(parseISO(date), dateFormat);
    } catch (err) {
        return date;
    }
}

/**
 * Converts ISO 8601 date range string to another date format
 * @param date ISO 8601 string
 * @param dateFormat output date format
 */
export function dateRangeFormatter(
    date: string,
    dateFormat: DateFormatEnum,
): string {
    const dates = date.split(' - ');
    const startDate = formatIsoString(dates[0], dateFormat);
    const endDate = formatIsoString(dates[1], dateFormat);
    return startDate + ' - ' + endDate;
}

/**
 * Get days in between two dates.
 * @param startDate Date
 * @param endDate Date
 * @return Date[]
 */
export function getDaysInBetween(startDate: Date, endDate: Date): Date[] {
    return [
        ...Array(differenceInDays(endDate, startDate) + 1).keys(),
    ].map((i) => addDays(startDate, i));
}

/**
 * Get weeks in between two dates.
 * @param startDate Date
 * @param endDate Date
 * @return Date[]
 */
export function getWeeksInBetween(startDate: Date, endDate: Date): Date[] {
    return [
        ...Array(differenceInWeeks(endDate, startDate) + 1).keys(),
    ].map((i) => addWeeks(startDate, i));
}

/**
 * Get months in between two dates.
 * @param startDate Date
 * @param endDate Date
 * @return Date[]
 */
export function getMonthsInBetween(startDate: Date, endDate: Date): Date[] {
    return [
        ...Array(differenceInMonths(endDate, startDate) + 1).keys(),
    ].map((i) => addMonths(startDate, i));
}

/**
 * Adjusts the date to the UTC time zone
 * ex.: 2021-05-31T18:00:00Z+02:00 => 2021-05-31T18:00:00Z+00:00
 * @param date
 */
export const adjustForUTCOffset = (date: Date) => {
    return new Date(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds(),
    );
};

/**
 * Remove zone information and convert to json date format
 * @param date
 * @return string
 */
export const removeZone = (date: Date): string => {
    return format(date, DateFormatEnum.JSON_DATE_WITHOUT_ZONE);
};

/**
 * Adjusts the date to the given time zone's date
 * @param date
 * @param ianaTimezone
 */
export const adjustForTimezone = (date: Date, ianaTimezone: string): Date => {
    return toDate(removeZone(date), { timeZone: ianaTimezone });
};

/**
 * Get the date in a given timezone
 * @param date
 * @param ianaTimezone
 * @param format date | datetime
 * @return ISO 8601 YYYY-mm-dd OR YYYY-mm-ddTHH-mm-dd
 */
export const getDateInTimeZone = (
    date: Date,
    ianaTimezone: string,
    format: 'date' | 'datetime' = 'date',
): string => {
    const options: DateTimeFormatOptions = {
        timeZone: ianaTimezone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour12: false,
    };
    const parts = new Intl.DateTimeFormat([], options).formatToParts(date);
    const year = parts.find((part) => part.type === 'year');
    const month = parts.find((part) => part.type === 'month');
    const day = parts.find((part) => part.type === 'day');
    const hour = parts.find((part) => part.type === 'hour');
    const minute = parts.find((part) => part.type === 'minute');
    const second = parts.find((part) => part.type === 'second');

    const datePart = year?.value + '-' + month?.value + '-' + day?.value;
    const timePart = hour?.value + ':' + minute?.value + ':' + second?.value;

    if (format === 'date') {
        return datePart;
    } else {
        return datePart + 'T' + timePart;
    }
};
